import React, { useCallback, useEffect } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { getAllFilterKeys } from 'components/Filters/utils';
import { FacilityPills, FreeCancellationPill, PropertyTypePills, ReviewScorePill, StarRatingsPill, ThemePills } from 'components/FiltersOverlay/FilterOverlaySummary/FilterOverlaySummary';
import { useFiltersForm } from 'hooks/useFiltersForm';
import { getAnchorHotelId, getShowTotalPriceToggle } from 'modules/sapiSearch/selectors';
import { Alert } from '@daedalus/atlas/Alert';
import { Button } from '@daedalus/atlas/Button';
import Wrapper from '@daedalus/atlas/helpers/ContentWrapper';
import { HorizontalScroll } from '@daedalus/atlas/helpers/HorizontalScroll';
import { Icon } from '@daedalus/atlas/Icon';
import { trackEvent } from '@daedalus/core/src/analytics/modules/actions';
import { Action, Category, Entity } from '@daedalus/core/src/analytics/types/Events';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
const titleMessage = defineMessage({
  id: 'mapResults.filterMatches',
  defaultMessage: '{count, plural, =0 {We found no matches} one {Only 1 exact match} other {Only # exact matches}}'
});
const ScrollWrapper = /*#__PURE__*/styled('div')({
  name: "ScrollWrapper",
  class: "sepu8xp",
  propsAsIs: false
});
const _exp3 = /*#__PURE__*/() => HorizontalScroll;
const HorizontalScrollComponent = /*#__PURE__*/styled(_exp3())({
  name: "HorizontalScrollComponent",
  class: "haksp2x",
  propsAsIs: true
});
const ClearAllWrapper = /*#__PURE__*/styled('div')({
  name: "ClearAllWrapper",
  class: "c1ae90f",
  propsAsIs: false
});
const VerticalDivider = /*#__PURE__*/styled('div')({
  name: "VerticalDivider",
  class: "vwspueq",
  propsAsIs: false
});
const _exp11 = /*#__PURE__*/() => Alert;
const AlertWrapper = /*#__PURE__*/styled(_exp11())({
  name: "AlertWrapper",
  class: "a1erfmgl",
  propsAsIs: true
});
const SubtitleWrapper = /*#__PURE__*/styled('span')({
  name: "SubtitleWrapper",
  class: "s1r0qtw7",
  propsAsIs: false
});
const _exp15 = /*#__PURE__*/() => Wrapper;
const ContentWrapper = /*#__PURE__*/styled(_exp15())({
  name: "ContentWrapper",
  class: "c1f58gi7",
  propsAsIs: true
});
const allFilterKeys = getAllFilterKeys();
interface Props {
  numResults: number;
}
const PillComponents = [FacilityPills, ThemePills, PropertyTypePills, StarRatingsPill, FreeCancellationPill, ReviewScorePill];
export const PartialMatchBanner = ({
  numResults
}: Props) => {
  const dispatch = useDispatch();
  const anchorHotelId = useSelector(getAnchorHotelId);
  const showTotalPriceToggle = useSelector(getShowTotalPriceToggle);
  const hasMarginTop = !anchorHotelId && !showTotalPriceToggle;
  const {
    getFilters,
    toggleFilters,
    toggleMultiFilter,
    submitFilters,
    clearFiltersAndApply
  } = useFiltersForm(allFilterKeys, 'PartialMatchBanner');
  const filters = getFilters();
  const trackPartialResultsBannerClick = useCallback(() => {
    dispatch(trackEvent({
      category: Category.User,
      entity: Entity.PartialResultsBanner,
      action: Action.Clicked
    }));
  }, [dispatch]);
  const handleClearClick = () => {
    trackPartialResultsBannerClick();
    clearFiltersAndApply();
  };
  const handleFilterChange = useCallback<typeof toggleFilters>(updatedFilters => {
    trackPartialResultsBannerClick();
    submitFilters(updatedFilters);
  }, [trackPartialResultsBannerClick, submitFilters]);
  const handleMultiFilterChange = useCallback<typeof toggleMultiFilter>((key, value) => {
    const removeMultiValue = (filterValue: string | string[] | number) => {
      if (typeof filterValue === 'string' || typeof filterValue === 'number') {
        return undefined;
      }
      return filterValue.filter(item => item !== value);
    };
    trackPartialResultsBannerClick();
    submitFilters({
      ...filters,
      [key]: removeMultiValue(filters[key])
    });
  }, [trackPartialResultsBannerClick, submitFilters, filters]);
  const {
    formatMessage
  } = useIntl();
  useEffect(() => {
    dispatch(trackEvent({
      category: Category.User,
      entity: Entity.PartialResultsBanner,
      action: Action.Viewed,
      payload: {
        matchesCount: numResults
      }
    }));
  }, [dispatch, numResults]);
  return <ContentWrapper data-id="PartialMatchBanner" className={cx(hasMarginTop && '__marginTop')}>
      <AlertWrapper dataId="PartialMatchBannerTitle" title={formatMessage(titleMessage, {
      count: numResults
    })} variant="warning">
        <SubtitleWrapper>
          <FormattedMessageWrapper id="mapNoResults.tryRemoveFilters" defaultMessage="Try adjusting your search criteria or removing some filters." />
        </SubtitleWrapper>

        <ScrollWrapper>
          <HorizontalScrollComponent rowGap="s200" columnGap="s200">
            <>
              {PillComponents.map((PillComponent, index) => <PillComponent key={`pill_${index}`} getFilters={getFilters} toggleMultiFilter={handleMultiFilterChange} toggleFilters={handleFilterChange} isActive={false} />)}
            </>
            <VerticalDivider />
            <ClearAllWrapper>
              <Button variant="quiet" size="md" iconStart={<Icon name="Trash2" />} onClick={handleClearClick}>
                <FormattedMessageWrapper id="clearAll" defaultMessage="Clear all" />
              </Button>
            </ClearAllWrapper>
          </HorizontalScrollComponent>
        </ScrollWrapper>
      </AlertWrapper>
    </ContentWrapper>;
};

require("./PartialMatchBanner.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./PartialMatchBanner.tsx");